import { ChangeDetectionStrategy, Component, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AuditoriaService } from '../../core/auditoria/auditoria.service';
import { LogAuditoria } from '../../core/auditoria/auditoria.model';

const PAGE_SIZE = 25;

@Component({
  selector: 'app-relatorios',
  standalone: true,
  imports: [CommonModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="page">
      <header class="page__top">
        <div class="page__heading">
          <h1 class="page__title">Auditoria</h1>
          <p class="page__sub">Quem criou, editou ou inativou cupons, gatilhos e permissoes de admin</p>
        </div>
        <div class="page__actions">
          <button class="btn" (click)="carregar()" [disabled]="loading()">
            {{ loading() ? 'Carregando…' : 'Recarregar' }}
          </button>
        </div>
      </header>

      <div class="filtros">
        <div class="filtros__group">
          <button class="chip" [class.chip--on]="entidade() === ''"  (click)="setEntidade('')">Tudo</button>
          <button class="chip" [class.chip--on]="entidade() === 'C'" (click)="setEntidade('C')">Cupons</button>
          <button class="chip" [class.chip--on]="entidade() === 'G'" (click)="setEntidade('G')">Gatilhos</button>
          <button class="chip" [class.chip--on]="entidade() === 'U'" (click)="setEntidade('U')">Usuarios</button>
        </div>
        <input class="inp" type="text" placeholder="Autor (login)..." [ngModel]="autor()" (ngModelChange)="autor.set($event)" (keyup.enter)="aplicar()" />
        <label class="dt">De <input type="date" [ngModel]="dtIni()" (ngModelChange)="dtIni.set($event)" /></label>
        <label class="dt">Ate <input type="date" [ngModel]="dtFim()" (ngModelChange)="dtFim.set($event)" /></label>
        <button class="btn btn--primary btn--sm" (click)="aplicar()">Filtrar</button>
        @if (temFiltro()) { <button class="btn btn--sm" (click)="limpar()">Limpar</button> }
      </div>

      @if (loading()) {
        <div class="state">Carregando…</div>
      } @else if (erro()) {
        <div class="alert alert--erro">{{ erro() }}</div>
      } @else if (itens().length === 0) {
        <div class="state">Nenhum registro de auditoria para os filtros atuais.</div>
      } @else {
        <table class="tbl">
          <thead>
            <tr>
              <th style="width: 130px">Data / hora</th>
              <th style="width: 130px">Area</th>
              <th>Registro</th>
              <th style="width: 100px">Acao</th>
              <th>Alteracao</th>
              <th style="width: 230px">Autor</th>
            </tr>
          </thead>
          <tbody>
            @for (r of itens(); track r.nrSequencia) {
              <tr>
                <td class="muted nowrap">{{ r.dtEvento | date:'dd/MM/yyyy HH:mm' }}</td>
                <td><span class="area">{{ r.dsEntidade }}</span></td>
                <td>{{ r.dsDetalhe || r.dsChave }}</td>
                <td>
                  <span class="op"
                        [class.op--i]="r.ieOperacao === 'I'"
                        [class.op--u]="r.ieOperacao === 'U'"
                        [class.op--d]="r.ieOperacao === 'D'">{{ r.dsOperacao }}</span>
                </td>
                <td>
                  @if (r.ieOperacao === 'U') {
                    <span class="campo">{{ r.dsCampo }}:</span>
                    <span class="antes">{{ r.dsValorAnterior || '∅' }}</span>
                    <span class="seta">→</span>
                    <span class="depois">{{ r.dsValorNovo || '∅' }}</span>
                  } @else {
                    <span class="muted">—</span>
                  }
                </td>
                <td>
                  <span class="autor" [title]="r.nmPessoaAutor || r.nmUsuarioAutor">{{ r.nmPessoaAutor || r.nmUsuarioAutor }}</span>
                  @if (r.nmPessoaAutor) { <span class="autor-login">{{ r.nmUsuarioAutor }}</span> }
                </td>
              </tr>
            }
          </tbody>
        </table>

        <div class="pager">
          <span class="pager__info">{{ rangeInicio() }}–{{ rangeFim() }} de {{ total() }} registros</span>
          <div class="pager__nav">
            <button class="btn btn--sm" (click)="irPara(1)" [disabled]="pagina() === 1">«</button>
            <button class="btn btn--sm" (click)="irPara(pagina() - 1)" [disabled]="pagina() === 1">‹ Anterior</button>
            <span class="pager__page">Pagina {{ pagina() }} de {{ totalPaginas() }}</span>
            <button class="btn btn--sm" (click)="irPara(pagina() + 1)" [disabled]="pagina() >= totalPaginas()">Proxima ›</button>
            <button class="btn btn--sm" (click)="irPara(totalPaginas())" [disabled]="pagina() >= totalPaginas()">»</button>
          </div>
        </div>
      }
    </div>
  `,
  styles: [`
    :host { display: block; }
    .page { padding: 32px 40px; max-width: 1280px; margin: 0 auto; font-family: var(--font-sans); color: rgb(var(--color-ink)); }
    .page__top { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 20px; border-bottom: 1px solid rgb(var(--color-line)); margin-bottom: 20px; gap: 16px; }
    .page__heading { display: flex; flex-direction: column; gap: 2px; }
    .page__title { margin: 0; font-size: 24px; font-weight: 700; letter-spacing: -0.02em; }
    .page__sub { margin: 0; color: rgb(var(--color-ink-3)); font-size: 13px; }
    .page__actions { display: flex; gap: 8px; flex-shrink: 0; padding-top: 4px; }

    .btn { padding: 9px 16px; border: 1px solid rgb(var(--color-line-2)); border-radius: var(--radius); background: rgb(var(--color-paper)); color: rgb(var(--color-ink)); cursor: pointer; font-size: 13px; font-weight: 500; font-family: inherit; }
    .btn:hover:not(:disabled) { background: rgb(var(--color-hover)); }
    .btn:disabled { opacity: 0.6; cursor: not-allowed; }
    .btn--primary { background: rgb(var(--color-accent)); color: #fff; border-color: rgb(var(--color-accent)); }
    .btn--primary:hover:not(:disabled) { background: rgb(var(--color-accent-2)); }
    .btn--sm { padding: 6px 12px; font-size: 12px; }

    .filtros { display: flex; gap: 8px; margin-bottom: 16px; align-items: center; flex-wrap: wrap; }
    .filtros__group { display: inline-flex; gap: 4px; }
    .chip { padding: 7px 14px; border: 1px solid rgb(var(--color-line-2)); border-radius: 999px; background: rgb(var(--color-paper)); color: rgb(var(--color-ink-2)); cursor: pointer; font-size: 12px; font-weight: 600; font-family: inherit; }
    .chip:hover { background: rgb(var(--color-hover)); }
    .chip--on { background: rgb(var(--color-accent)); color: #fff; border-color: rgb(var(--color-accent)); }
    .inp { padding: 7px 12px; border: 1px solid rgb(var(--color-line-2)); border-radius: var(--radius); font-size: 13px; min-width: 180px; outline: none; font-family: inherit; color: rgb(var(--color-ink)); background: rgb(var(--color-paper)); }
    .inp:focus { border-color: rgb(var(--color-accent)); }
    .dt { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; color: rgb(var(--color-ink-3)); }
    .dt input { padding: 6px 10px; border: 1px solid rgb(var(--color-line-2)); border-radius: var(--radius); font-size: 13px; font-family: inherit; color: rgb(var(--color-ink)); background: rgb(var(--color-paper)); }

    .tbl { width: 100%; border-collapse: collapse; font-size: 13px; }
    .tbl thead th { text-align: left; padding: 10px 12px; border-bottom: 2px solid rgb(var(--color-line)); font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; color: rgb(var(--color-ink-3)); }
    .tbl tbody td { padding: 11px 12px; border-bottom: 1px solid rgb(var(--color-line)); vertical-align: middle; }
    .tbl tbody tr:hover { background: rgb(var(--color-bg-soft)); }
    .muted { color: rgb(var(--color-ink-3)); }
    .nowrap { white-space: nowrap; font-variant-numeric: tabular-nums; }

    .area { display: inline-block; white-space: nowrap; font-size: 11px; font-weight: 600; padding: 2px 8px; border-radius: 999px; background: rgb(var(--color-bg-soft)); color: rgb(var(--color-ink-2)); }

    .op { font-size: 11px; font-weight: 700; padding: 2px 9px; border-radius: 999px; }
    .op--i { background: rgb(var(--color-accent-bg)); color: rgb(var(--color-accent-2)); }
    .op--u { background: rgb(var(--color-info-bg)); color: rgb(var(--color-info)); }
    .op--d { background: rgb(var(--color-danger-bg)); color: rgb(var(--color-danger)); }

    .campo { font-weight: 600; color: rgb(var(--color-ink-2)); margin-right: 4px; }
    .antes { color: rgb(var(--color-danger)); text-decoration: line-through; opacity: .8; }
    .seta { margin: 0 6px; color: rgb(var(--color-ink-3)); }
    .depois { color: rgb(var(--color-accent-2)); font-weight: 600; }

    .autor { display: block; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .autor-login { display: block; font-size: 11px; color: rgb(var(--color-ink-3)); font-variant-numeric: tabular-nums; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

    .state { padding: 48px; text-align: center; color: rgb(var(--color-ink-3)); font-size: 14px; }
    .alert--erro { background: rgb(var(--color-danger-bg)); border: 1px solid rgb(var(--color-danger)); color: rgb(var(--color-danger)); padding: 12px 16px; border-radius: var(--radius); }

    .pager { display: flex; justify-content: space-between; align-items: center; margin-top: 16px; flex-wrap: wrap; gap: 12px; }
    .pager__info { font-size: 12px; color: rgb(var(--color-ink-3)); }
    .pager__nav { display: flex; gap: 4px; align-items: center; }
    .pager__page { font-size: 12px; color: rgb(var(--color-ink-2)); margin: 0 8px; }

    @media (max-width: 768px) { .page { padding: 20px 16px; } .tbl { font-size: 12px; } }
  `],
})
export class RelatoriosComponent implements OnInit {
  private readonly svc = inject(AuditoriaService);

  protected readonly itens = signal<LogAuditoria[]>([]);
  protected readonly total = signal(0);
  protected readonly pagina = signal(1);
  protected readonly loading = signal(false);
  protected readonly erro = signal<string | null>(null);

  protected readonly entidade = signal<string>('');
  protected readonly autor = signal('');
  protected readonly dtIni = signal('');
  protected readonly dtFim = signal('');

  protected readonly totalPaginas = computed(() => Math.max(1, Math.ceil(this.total() / PAGE_SIZE)));
  protected readonly rangeInicio = computed(() => (this.total() === 0 ? 0 : (this.pagina() - 1) * PAGE_SIZE + 1));
  protected readonly rangeFim = computed(() => Math.min(this.pagina() * PAGE_SIZE, this.total()));
  protected readonly temFiltro = computed(() => !!(this.entidade() || this.autor() || this.dtIni() || this.dtFim()));

  ngOnInit(): void { this.carregar(); }

  carregar(): void {
    this.loading.set(true);
    this.erro.set(null);
    this.svc.consultar({
      ieEntidade: this.entidade() || undefined,
      autor: this.autor().trim() || undefined,
      dtIni: this.dtIni() || undefined,
      dtFim: this.fimExclusivo(),
      skip: (this.pagina() - 1) * PAGE_SIZE,
      take: PAGE_SIZE,
    }).subscribe({
      next: res => {
        this.itens.set(res.data?.items ?? []);
        this.total.set(res.data?.total ?? 0);
        this.loading.set(false);
      },
      error: e => { this.erro.set(e?.error?.error?.message ?? e?.message ?? 'Falha ao carregar auditoria.'); this.loading.set(false); },
    });
  }

  // dtFim no back e exclusivo (< dtFim); soma 1 dia p/ a data escolhida ser inclusiva.
  private fimExclusivo(): string | undefined {
    const f = this.dtFim();
    if (!f) return undefined;
    const d = new Date(f + 'T00:00:00');
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  }

  setEntidade(e: string): void { this.entidade.set(e); this.pagina.set(1); this.carregar(); }
  aplicar(): void { this.pagina.set(1); this.carregar(); }
  limpar(): void { this.entidade.set(''); this.autor.set(''); this.dtIni.set(''); this.dtFim.set(''); this.pagina.set(1); this.carregar(); }

  irPara(p: number): void {
    const destino = Math.min(Math.max(1, p), this.totalPaginas());
    if (destino === this.pagina()) return;
    this.pagina.set(destino);
    this.carregar();
  }
}
